import axios from 'axios';
import { log } from 'util/log';
import { store } from 'views/App/Redux';

/**
 * Downloads a CSV file from a specified URL with authentication
 *
 * @param url - The URL to download the CSV from
 * @param filename - Optional. The name to save the file as when downloaded (for browser use)
 * @returns Promise that resolves to the CSV data as a string
 */
export const downloadCSV = async (url, filename) => {
  log('%cGET CSV%c', 'color: blue', 'color: black', url);

  // Get the authentication token from your Redux store
  const token = store.getState().auth.token.payload.token;

  // Set up headers with the correct content type for CSV
  const headers = {
    'Accept': 'text/csv,application/csv,text/plain',
    'Authorization': token,
  };

  try {
    // Make the request with axios using your existing pattern
    const response = await axios({
      method: 'get',
      url: url,
      headers: headers,
      responseType: 'blob', // Important for file downloads
    });

    // For browser environments - trigger file download
    if (typeof window !== 'undefined' && filename) {
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object after download
      window.URL.revokeObjectURL(downloadUrl);

      return 'CSV downloaded successfully';
    }

    // For Node.js environments or when not downloading directly
    if (response.data instanceof Blob) {
      // Convert blob to text for further processing
      return await response.data.text();
    }

    return response.data;
  } catch (error) {
    log('%cERROR%c', 'color: red', 'color: black', 'Failed to download CSV:', error);

    // Enhance error message based on type of error
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(`Failed to download CSV: Server responded with status ${error.response.status}`);
      } else if (error.request) {
        throw new Error('Failed to download CSV: No response received from server');
      }
    }

    throw new Error(`Failed to download CSV: ${error.message}`);
  }
};

/**
 * Downloads and parses a CSV file from a specified URL with authentication
 *
 * @param url - The URL to download the CSV from
 * @returns Promise that resolves to the parsed CSV data as an array of objects
 */
export const getCSVData = async (url) => {
  log('%cGET CSV DATA%c', 'color: blue', 'color: black', url);

  // Get the authentication token from your Redux store
  const token = store.getState().auth.token.payload.token;

  try {
    // Make the request with axios using your existing pattern
    const response = await axios({
      method: 'get',
      url: url,
      headers: {
        'Accept': 'text/csv,application/csv,text/plain',
        'Authorization': token,
      },
      responseType: 'text', // Get as text for parsing
    });

    // Parse CSV data (basic implementation - consider using a library like Papa Parse for production)
    const lines = response.data.split('\n');
    const headers = lines[0].split(',').map((h) => h.trim());

    const results = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const values = line.split(',');
      const item = {};

      headers.forEach((header, index) => {
        const value = values[index]?.trim() ?? '';

        // Basic type conversion
        if (value === '') {
          item[header] = null;
        } else if (!isNaN(Number(value))) {
          item[header] = Number(value);
        } else if (value.toLowerCase() === 'true') {
          item[header] = true;
        } else if (value.toLowerCase() === 'false') {
          item[header] = false;
        } else {
          item[header] = value;
        }
      });

      results.push(item);
    }

    return results;
  } catch (error) {
    log('%cERROR%c', 'color: red', 'color: black', 'Failed to get CSV data:', error);

    // Enhance error message based on type of error
    if (axios.isAxiosError(error)) {
      if (error.response) {
        throw new Error(`Failed to get CSV data: Server responded with status ${error.response.status}`);
      } else if (error.request) {
        throw new Error('Failed to get CSV data: No response received from server');
      }
    }

    throw new Error(`Failed to get CSV data: ${error.message}`);
  }
};


export const getData = async (url) => {
  log('%cGET%c', 'color: blue', 'color: black', url);

  const token = store.getState().auth.token.payload.token;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: token,
  };
  return await axios({
    method: 'get',
    url: url,
    headers: headers,
  });
};


export const getGzipData = async (url) => {
  log('%cGET%c', 'color: blue', 'color: black', url);

  const token = store.getState().auth.token.payload.token;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: token,
  };
  return await axios({
    method: 'get',
    url: url,
    responseType: 'arraybuffer',
    decompress: true,
    headers: headers,
  });
};


export const getDataWithoutAuth = async (url) => {
  log('%cGET%c', 'color: blue', 'color: black', url);

  const headers = {
    'Content-Type': 'application/json',
  };
  return await axios({
    method: 'get',
    url: url,
    headers: headers,
  });
};

export const postData = async (url, data, noToken) => {
  log('%cPOST%c', 'color: blue', 'color: black', url, data);

  let headers = {
    'Content-Type': 'application/json',
  };
  if (!noToken) {
    const token = store.getState().auth.token.payload.token;
    headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
  }
  return await axios({
    method: 'post',
    url: url,
    data: data,
    headers: headers,
  });
};
